import { Injectable, computed, signal } from '@angular/core';
import { ViewItem } from '../models/viewItem.model';

export interface ViewDialogMode {
  mode: 'create' | 'update' | 'overwrite';
  isStandardView: boolean;
  targetViewType: 'standard' | 'custom';
}

@Injectable({
  providedIn: 'root',
})
export class ViewDialogService {
  // UI State
  public readonly isProgrammaticUpdate = signal(false);
  public readonly saveViewDialogVisible = signal<boolean>(false);
  public readonly newViewName = signal<string>('');
  public readonly originalViewName = signal<string>('');
  public readonly isStandardView = signal<boolean>(false);
  public readonly viewToDelete = signal<string | null>(null);
  public readonly saveViewRequested = signal<boolean>(false);
  public readonly viewSelectionRequested = signal<string | null>(null);
  public readonly viewDeletionRequested = signal<string | null>(null);
  public readonly closeDropdownRequested = signal<boolean>(false);

  // Dialog State
  private readonly _currentName = signal<string>('');
  private readonly _originalName = signal<string>('');
  private readonly _isStandardViewChecked = signal<boolean>(false);
  private readonly _selectedViewData = signal<ViewItem | undefined>(undefined);
  private readonly _standardViews = signal<ViewItem[]>([]);
  private readonly _userViews = signal<ViewItem[]>([]);
  private readonly _isAdmin = signal<boolean>(false);

  private readonly _isEditingExistingView = computed(() => {
    return this._selectedViewData() !== undefined && this._originalName().length > 0;
  });

  private readonly _hasNameChanged = computed(() => {
    return this._currentName() !== this._originalName();
  });

  private readonly _conflictingView = computed(() => {
    if (!this._currentName()) {
      return undefined;
    }

    const allViews = [...this._standardViews(), ...this._userViews()];
    return allViews.find(
      (view) => view.name.toLowerCase() === this._currentName().toLowerCase() && view.id !== this._selectedViewData()?.id,
    );
  });

  private readonly _hasConflict = computed(() => this._conflictingView() !== undefined);

  private readonly _conflictingViewType = computed(() => {
    if (!this._conflictingView()) return undefined;
    return this._conflictingView()!.isShared ? 'standard' : 'custom';
  });

  private readonly _targetViewType = computed(() => {
    return this._isStandardViewChecked() ? 'standard' : 'custom';
  });

  private readonly _hasConflictWithTargetType = computed(() => {
    return this._hasConflict() && this._conflictingViewType() === this._targetViewType();
  });

  private readonly _hasConflictWithTargetTypeOnSwitch = computed(() => {
    if (!this._currentName() || !this._selectedViewData()) {
      return false;
    }

    const allViews = [...this._standardViews(), ...this._userViews()];
    const conflictingViewWithTargetType = allViews.find(
      (view) =>
        view.name.toLowerCase() === this._currentName().toLowerCase() &&
        view.id !== this._selectedViewData()!.id &&
        (this._targetViewType() === 'standard' ? view.isShared : !view.isShared),
    );

    return conflictingViewWithTargetType !== undefined;
  });

  private readonly _conflictingViewForTargetType = computed(() => {
    if (!this._currentName() || !this._selectedViewData()) {
      return undefined;
    }

    const allViews = [...this._standardViews(), ...this._userViews()];
    return allViews.find(
      (view) =>
        view.name.toLowerCase() === this._currentName().toLowerCase() &&
        view.id !== this._selectedViewData()!.id &&
        (this._targetViewType() === 'standard' ? view.isShared : !view.isShared),
    );
  });

  private readonly _hasConflictForNewView = computed(() => {
    if (!this._currentName() || this._selectedViewData()) {
      return false;
    }

    const allViews = [...this._standardViews(), ...this._userViews()];
    const conflictingViewWithTargetType = allViews.find(
      (view) =>
        view.name.toLowerCase() === this._currentName().toLowerCase() &&
        (this._targetViewType() === 'standard' ? view.isShared : !view.isShared),
    );

    return conflictingViewWithTargetType !== undefined;
  });

  private readonly _conflictingViewForNewView = computed(() => {
    if (!this._currentName() || this._selectedViewData()) {
      return undefined;
    }

    const allViews = [...this._standardViews(), ...this._userViews()];
    return allViews.find(
      (view) =>
        view.name.toLowerCase() === this._currentName().toLowerCase() &&
        (this._targetViewType() === 'standard' ? view.isShared : !view.isShared),
    );
  });

  private readonly _dialogMode = computed((): ViewDialogMode => {
    if (this._isEditingExistingView() && !this._hasNameChanged()) {
      const currentViewType = this._selectedViewData()?.isShared ? 'standard' : 'custom';
      const isSameType = currentViewType === this._targetViewType();

      if (isSameType) {
        return {
          mode: 'update',
          isStandardView: this._isStandardViewChecked(),
          targetViewType: this._targetViewType(),
        };
      } else {
        if (this._hasConflictWithTargetTypeOnSwitch()) {
          return {
            mode: 'overwrite',
            isStandardView: this._isStandardViewChecked(),
            targetViewType: this._targetViewType(),
          };
        } else {
          return {
            mode: 'create',
            isStandardView: this._isStandardViewChecked(),
            targetViewType: this._targetViewType(),
          };
        }
      }
    }

    if (this._hasConflictWithTargetType()) {
      return {
        mode: 'overwrite',
        isStandardView: this._isStandardViewChecked(),
        targetViewType: this._targetViewType(),
      };
    }

    if (this._hasConflictForNewView()) {
      return {
        mode: 'overwrite',
        isStandardView: this._isStandardViewChecked(),
        targetViewType: this._targetViewType(),
      };
    }

    return {
      mode: 'create',
      isStandardView: this._isStandardViewChecked(),
      targetViewType: this._targetViewType(),
    };
  });

  public readonly dialogActionText = computed(() => {
    return this._dialogMode().mode === 'create' ? 'Save' : 'Update';
  });

  public readonly shouldShowConflictMessage = computed(() => {
    return this._dialogMode().mode === 'overwrite' || this._dialogMode().mode === 'update';
  });

  public readonly conflictMessageText = computed(() => {
    if (this._dialogMode().mode === 'update') {
      return 'The existing view will be updated with the current settings.';
    } else if (this._dialogMode().mode === 'overwrite') {
      return 'A view with this name already exists and will be updated.';
    }
    return '';
  });

  public readonly canShowCheckbox = computed(() => this._isAdmin());

  public openSaveViewDialog(): void {
    this._currentName.set(this.newViewName());
    this._originalName.set(this.originalViewName());
    this._isStandardViewChecked.set(this.isStandardView());

    this.saveViewDialogVisible.set(true);
  }

  public closeSaveViewDialog(): void {
    this.saveViewDialogVisible.set(false);
  }

  public resetDialogState(): void {
    this.newViewName.set('');
    this.originalViewName.set('');
    this.isStandardView.set(false);
    this.saveViewDialogVisible.set(false);
  }

  public setDialogData(name: string, originalName: string): void {
    this.newViewName.set(name);
    this.originalViewName.set(originalName);
    this.isStandardView.set(false);
  }

  public setViewToDelete(viewId: string | null): void {
    this.viewToDelete.set(viewId);
  }

  public resetDeleteState(): void {
    this.viewToDelete.set(null);
  }

  public requestSaveView(): void {
    this.saveViewRequested.set(true);
  }

  public resetSaveViewRequest(): void {
    this.saveViewRequested.set(false);
  }

  public requestViewSelection(viewId: string): void {
    this.viewSelectionRequested.set(viewId);
  }

  public resetViewSelectionRequest(): void {
    this.viewSelectionRequested.set(null);
  }

  public requestViewDeletion(viewId: string): void {
    this.viewDeletionRequested.set(viewId);
  }

  public resetViewDeletionRequest(): void {
    this.viewDeletionRequested.set(null);
  }

  public requestDropdownClose(): void {
    this.closeDropdownRequested.set(true);
  }

  public resetDropdownCloseRequest(): void {
    this.closeDropdownRequested.set(false);
  }

  public setCurrentName(name: string): void {
    this._currentName.set(name);
  }

  public setOriginalName(name: string): void {
    this._originalName.set(name);
  }

  public setIsStandardViewChecked(checked: boolean): void {
    this._isStandardViewChecked.set(checked);
  }

  public setSelectedViewData(viewData: ViewItem | undefined): void {
    this._selectedViewData.set(viewData);
  }

  public setStandardViews(views: ViewItem[]): void {
    this._standardViews.set(views);
  }

  public setUserViews(views: ViewItem[]): void {
    this._userViews.set(views);
  }

  public setIsAdmin(isAdmin: boolean): void {
    this._isAdmin.set(isAdmin);
  }

  public initializeState(
    currentName: string,
    originalName: string,
    isStandardViewChecked: boolean,
    selectedViewData: ViewItem | undefined,
    standardViews: ViewItem[],
    userViews: ViewItem[],
    isAdmin: boolean,
  ): void {
    this._currentName.set(currentName);
    this._originalName.set(originalName);
    this._isStandardViewChecked.set(isStandardViewChecked);
    this._selectedViewData.set(selectedViewData);
    this._standardViews.set(standardViews);
    this._userViews.set(userViews);
    this._isAdmin.set(isAdmin);
  }

  public reset(): void {
    this._currentName.set('');
    this._originalName.set('');
    this._isStandardViewChecked.set(false);
    this._selectedViewData.set(undefined);

    this.newViewName.set('');
    this.originalViewName.set('');
    this.isStandardView.set(false);
  }

  public getConflictInfo() {
    const dialogMode = this._dialogMode();
    let conflictingView = this._conflictingView();

    if (dialogMode.mode === 'overwrite' && this._hasConflictWithTargetTypeOnSwitch()) {
      conflictingView = this._conflictingViewForTargetType();
    }

    if (dialogMode.mode === 'overwrite' && this._hasConflictForNewView()) {
      conflictingView = this._conflictingViewForNewView();
    }

    return {
      hasConflict: this._hasConflict(),
      conflictingView: conflictingView,
      hasConflictWithTargetType: this._hasConflictWithTargetType(),
      isEditingExistingView: this._isEditingExistingView(),
      hasNameChanged: this._hasNameChanged(),
      dialogMode: dialogMode,
    };
  }
}
